import React, { useEffect, useMemo, useState } from 'react';

import { LOAD_MORE_PAGE_SIZE } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { TableView } from '~components/table-view';

import { BulkSignupWizard } from '~direct-debits/components/bulk-signup-wizard';
import { columnsFactory } from '~direct-debits/forms/client-signups/list';
import { searchFields } from '~direct-debits/forms/client-signups/search';

import { useGetClientSignupsQuery } from '~direct-debits/actions/clientSignups';
import { useGetConnectionsQuery } from '~direct-debits/actions/connections';
import { setTitle } from '~main/actions/login';

import SignupStatsCards from './SignupStatsCards';

interface ComponentProps {
  isActive?: boolean;
}

const ClientSignupsContainer: React.FC<ComponentProps> = ({ isActive = true }) => {
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [searchParams, setParams] = useSearch('dd-client-signups-search', { limit: LOAD_MORE_PAGE_SIZE });

  const handleSearchSubmit = (values: Record<string, any>) => setParams({ ...values, limit: LOAD_MORE_PAGE_SIZE });

  // Refetch on tab re-entry so signup statuses stay fresh without a manual
  // reload (TAV-2842) — mirrors the payment-batches and mandate-matching tabs.
  const { data, isFetching, isError, error } = useGetClientSignupsQuery(searchParams, {
    skip: !isActive,
    refetchOnMountOrArgChange: true,
  });
  const { data: connections } = useGetConnectionsQuery();
  const hasActiveConnection = (connections ?? []).some((c) => c.status === 'active');
  // TableView rows require an `id` field — client_id is the stable key for each row.
  const rows = (data?.rows ?? []).map((row) => ({ ...row, id: row.client_id }));
  const total = data?.total ?? 0;
  const stats = data?.stats;
  const columns = useMemo(() => columnsFactory({ isConnected: hasActiveConnection }), [hasActiveConnection]);

  useEffect(() => {
    if (isActive) setTitle('Direct Debits — Client Signups');
  }, [isActive]);

  const handleLoadMore = () => {
    const currentLimit = Number(searchParams.limit) || LOAD_MORE_PAGE_SIZE;
    setParams({ ...searchParams, limit: currentLimit + LOAD_MORE_PAGE_SIZE });
  };

  return (
    <div className="p-2 dd-client-signups">
      <SignupStatsCards stats={stats} isLoading={isFetching && !data} />
      <Toolbar
        id="dd-client-signups"
        rightCluster={
          <>
            <Button
              id="direct-debits-bulk-signup-button"
              data-cy="direct-debits-bulk-signup-button"
              color="success"
              size="sm"
              className="me-2"
              onClick={() => setWizardOpen(true)}
              disabled={!hasActiveConnection}
              title={hasActiveConnection ? undefined : 'GoCardless connection is not active'}
            >
              Send Bulk Signup Links
            </Button>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="dd-client-signups-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              ariaLabel="Toggle search bar"
            />
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      {isError ? (
        <ErrorResponse message={getErrorMessage(error)} />
      ) : (
        <TableView
          id="dd-client-signups-table"
          columns={columns}
          data={rows}
          isLoading={isFetching}
          emptyMessage="No clients found"
          readonly
          striped
          allowPagination
          totalCount={total}
          onLoadMore={handleLoadMore}
          showCount
          pageSize={Number(searchParams.limit) || LOAD_MORE_PAGE_SIZE}
        />
      )}
      <BulkSignupWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
    </div>
  );
};

export default ClientSignupsContainer;
